import React, { useState, KeyboardEvent, useMemo, useEffect } from 'react';
import DefaultChip from 'components/Utils/DefaultChip';
import './style.scss';
import DefaultAutoComplete from '../DefaultAutoComplete';
import _ from 'lodash';

type TagInputProps = {
  initialTags?: string[];
  onChange: (tags: string[]) => void;
  suggestions?: string[];
  placeholder?: string;
  disabled?: boolean;
};

const TagInput: React.FC<TagInputProps> = ({
  initialTags = [],
  onChange,
  suggestions = [],
  placeholder = 'Add tags (Enter or comma to submit)',
  disabled = false,
}) => {
  const [tags, setTags] = useState(initialTags);
  const [inputValue, setInputValue] = useState('');

  const finalTags = useMemo(() => {
    return [..._.uniq(tags), inputValue].filter((t) => !!t);
  }, [tags, inputValue]);

  useEffect(() => {
    onChange(finalTags);
  }, [finalTags, onChange]);

  const handleAddTag = (tag?: string) => {
    if (!tag) return;
    const trimmed = tag.trim();
    if (trimmed && !tags.includes(trimmed)) {
      setTags((prevState) => [...prevState, trimmed]);
    }
    setInputValue('');
  };

  const handleRemoveTag = (tagToRemove: string) => {
    setTags((prevState) => prevState.filter((t) => t !== tagToRemove));
    if (inputValue === tagToRemove) setInputValue('');
  };

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>, isSelection: boolean) => {
    if (!isSelection && (event.key === 'Enter' || event.key === ',') && inputValue.trim()) {
      event.preventDefault();
      handleAddTag(inputValue);
    }
  };

  const handleInputChange = (newValue: string) => {
    if (newValue === null) return;
    if (newValue?.includes(',')) {
      const newTags = newValue
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);
      newTags.forEach(handleAddTag);
      setInputValue('');
    } else {
      setInputValue(newValue);
    }
  };

  const availableSuggestions = useMemo(() => {
    return _.uniq(suggestions.filter((s) => !tags.includes(s)));
  }, [suggestions, tags]);

  if (!finalTags?.length && disabled) return null;

  return (
    <div className={`tag-input ${disabled ? 'disabled' : ''}`}>
      {finalTags?.length > 0 && (
        <div className="tag-chips">
          {finalTags.map((tag) => (
            <DefaultChip
              key={tag}
              text={tag}
              backgroundColor="#e3f2fd"
              textColor="#1976d2"
              onDelete={disabled ? undefined : () => handleRemoveTag(tag)}
            />
          ))}
        </div>
      )}
      {!disabled && (
        <DefaultAutoComplete
          //@ts-ignore
          id="tag-input-field"
          name="tag-input-field"
          style={{ width: '100%' }}
          placeholder={placeholder}
          popOut={true}
          onKeyDown={(event: any, isSelection: any) => handleKeyDown(event, isSelection)}
          input={{
            value: inputValue,
            onChange: (value: string) => handleInputChange(value),
            onKeyDown: (event: React.KeyboardEvent<HTMLInputElement>, isSelection: boolean) =>
              handleKeyDown(event, isSelection),
          }}
          onUpdateInput={(value: any) => handleInputChange(value)}
          dataSource={availableSuggestions}
          onOptionSelected={(option: any) => {
            setTimeout(() => {
              handleAddTag(option);
              setInputValue('');
            }, 10);
          }}
        />
      )}
    </div>
  );
};

export default TagInput;
